// Sprint ONBOARD ONB.C1 (A.3) — Setup Wizard shell.
//
// Focused full-page setup view for the assigned Technical Lead (or the Buyer
// while self-handling). Renders the FULL setup_tasks list with inline task
// expansion, a "Hand back to {Buyer}" action, and a completion view. State is
// server-side (setup_tasks) — this page is a pure observer + launcher, polling
// GET /setup-project every 10s (matching OnboardingPanel).
//
// Access is server-enforced: GET /setup-project returns 403
// SETUP_GATE_FORBIDDEN for ineligible callers. We never render protected
// content before the first fetch resolves, so there is no flash-of-protected-
// content on the redirect path.
//
// Integration seam (A.4/A.5): each task's "Start" navigates to
// TASK_COPY[taskType].ctaPath. authorize_gdrive → /setup/authorize-gdrive (A.5)
// owns its 4-step flow end-to-end and advances its own setup_tasks row; this
// page reflects the change on the next poll. connect_github was rerouted off the
// unbuilt /setup/connect-github stub to the working Settings GitHub control
// (#1274 interim; #830 builds the dedicated sub-wizard).

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useMyTenant } from '../hooks/useMyTenant';
import {
  TASK_COPY,
  PHASE_LABELS,
  STATUS_PILL,
  type SetupTaskType,
  type SetupTaskStatus,
} from '../lib/setupTaskCopy';
// #1247 follow-on — the hub's invite_technical_lead CTA now opens this SHARED
// modal (same one the dashboard OnboardingPanel uses) instead of mis-routing to
// /settings/organisation's generic team-invite modal (which never assigned the
// TL). One modal, two surfaces — the invite wiring can't drift.
import InviteTechnicalLeadModal from '../components/InviteTechnicalLeadModal';
// #1274 F4 / #1277 — connect_github opens this shared modal (member-safe,
// setup-scoped connect) instead of the admin-only Settings control.
import SetupGithubConnectModal from '../components/SetupGithubConnectModal';

// ── Payload shape (mirrors setupProjectPanelService + the buyerName enrichment
//    added by GET /setup-project) ─────────────────────────────────────────────

interface WizardTask {
  id:          string;
  taskType:    SetupTaskType;
  ring:        0 | 1 | 2 | 3;
  status:      SetupTaskStatus;
  assignee:    { userId: string; name: string; avatarUrl: string | null } | null;
  skipAllowed: boolean;
  startedAt:   string | null;
  completedAt: string | null;
}

interface WizardSetupProject {
  id:                  string;
  tenantId:            string;
  currentPhase:        0 | 1 | 2 | 3;
  status:              'in_progress' | 'paused';
  buyerUserId:         string;
  technicalLeadUserId: string | null;
  tierClass:           'free_trial' | 'growth' | 'individual';
  completionPct:       number;
  createdAt:           string;
  updatedAt:           string;
}

interface SetupWizardPayload {
  setupProject:      WizardSetupProject;
  tasks:             WizardTask[];
  pendingInvitation: unknown;
  buyerName:         string | null;
}

const POLL_INTERVAL_MS = 10_000;

// choose_export_folder is withdrawn (#1151) and stays withdrawn: Sprint DRIVE
// (#2877), which superseded Sprint EXPORT, files into a BluePrint-managed folder
// tree, so there is no export folder to choose. Rows seeded before #1151 still
// render here, skippable.
const EXPORT_DEFERRED: SetupTaskType = 'choose_export_folder';

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const { myTenant } = useMyTenant();

  // undefined = initial load; null = no active setup project; payload = ready.
  const [data, setData] = useState<SetupWizardPayload | null | undefined>(undefined);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  // Hand-back modal state.
  const [showHandBack, setShowHandBack] = useState(false);
  const [handBackMessage, setHandBackMessage] = useState('');
  const [handingBack, setHandingBack] = useState(false);
  const [handBackError, setHandBackError] = useState<string | null>(null);

  // Tech Lead invite modal visibility (#1247 follow-on). The shared modal owns
  // the form + POST; on success the hub closes it and refetches via fetchWizard.
  const [showInviteModal, setShowInviteModal] = useState(false);
  // GitHub connect modal visibility (#1274 F4 / #1277).
  const [showGithubModal, setShowGithubModal] = useState(false);

  const unmounted = useRef(false);
  // Once we redirect on a 403 gate, stop polling / setState.
  const redirected = useRef(false);

  const fetchWizard = useCallback((): Promise<void> => {
    return apiFetch<SetupWizardPayload | undefined>('/setup-project')
      .then(res => {
        if (unmounted.current || redirected.current) return;
        // 204 → undefined → no active setup project.
        setData(res ?? null);
      })
      .catch((err: unknown) => {
        if (unmounted.current || redirected.current) return;
        // Server-enforced gate: ineligible caller → bounce to dashboard with a
        // notice. Redirect BEFORE any protected content renders (data stays
        // `undefined` → loading state only).
        if (err instanceof ApiError && err.status === 403) {
          redirected.current = true;
          navigate('/dashboard?notice=setup_forbidden', { replace: true });
          return;
        }
        // Other errors: keep last successful state (no toast in V1).
      });
  }, [navigate]);

  useEffect(() => {
    unmounted.current = false;
    void fetchWizard();
    const interval = setInterval(() => {
      if (unmounted.current || redirected.current) return;
      void fetchWizard();
    }, POLL_INTERVAL_MS);
    return () => {
      unmounted.current = true;
      clearInterval(interval);
    };
  }, [fetchWizard]);

  const handleStart = useCallback((taskType: SetupTaskType) => {
    if (taskType === EXPORT_DEFERRED) return; // deferred — no navigation
    // #1247 follow-on — invite_technical_lead opens the shared invite modal
    // here (it has no ctaPath); #1274 F4 — connect_github opens the shared
    // GitHub-connect modal (member-safe, setup-scoped). All other tasks
    // navigate to their destination.
    if (taskType === 'invite_technical_lead') {
      setShowInviteModal(true);
      return;
    }
    if (taskType === 'connect_github') {
      setShowGithubModal(true);
      return;
    }
    const path = TASK_COPY[taskType].ctaPath;
    if (path) navigate(path);
  }, [navigate]);

  // #1274 — skip a skip-allowed task from the hub. Mirrors OnboardingPanel's
  // skip: PATCH the shared skip endpoint, then refetch via the hub's existing
  // poll (no new poller). Used by the deferred row (choose_export_folder): you
  // can't DO it (withdrawn, see EXPORT_DEFERRED) but you CAN skip it so ring 2 can clear.
  const handleSkip = useCallback((taskId: string): Promise<void> => {
    return apiFetch(`/setup-tasks/${taskId}/skip`, { method: 'PATCH' })
      .then(() => {
        if (unmounted.current || redirected.current) return;
        return fetchWizard();
      })
      .catch(() => {
        // No-op — leave previous state intact (matches the panel's lean handling).
      });
  }, [fetchWizard]);

  const handleHandBack = useCallback((): Promise<void> => {
    if (!data?.setupProject) return Promise.resolve();
    setHandingBack(true);
    setHandBackError(null);
    const setupProjectId = data.setupProject.id;
    const body = handBackMessage.trim() ? { message: handBackMessage.trim() } : {};
    return apiFetch(`/setup-projects/${setupProjectId}/hand-back`, {
      method: 'POST',
      body:   JSON.stringify(body),
    })
      .then(() => {
        if (unmounted.current) return;
        setShowHandBack(false);
        navigate('/dashboard');
      })
      .catch((err: unknown) => {
        if (unmounted.current) return;
        setHandBackError(err instanceof Error ? err.message : 'Could not hand back. Please try again.');
      })
      .finally(() => {
        if (!unmounted.current) setHandingBack(false);
      });
  }, [data, handBackMessage, navigate]);

  // ── Render guards ──────────────────────────────────────────────────────────

  // Initial load OR mid-redirect → loading skeleton (NEVER protected content).
  if (data === undefined) {
    return (
      <div data-testid="setup-wizard-loading" style={pageStyle}>
        <div style={{ ...cardStyle, animation: 'none' }}>
          <div className="animate-pulse" style={{ height: 24, width: 280, background: 'var(--surface-alt)', borderRadius: 8, marginBottom: 16 }} />
          <div className="animate-pulse" style={{ height: 8, width: '100%', background: 'var(--surface-alt)', borderRadius: 4 }} />
        </div>
      </div>
    );
  }

  // No active setup project for this user.
  if (data === null) {
    return (
      <div data-testid="setup-wizard-empty" style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Nothing to set up right now</h1>
          <p style={subtitleStyle}>There is no active BluePrint setup assigned to you.</p>
          <button style={primaryBtnStyle} onClick={() => navigate('/dashboard')}>Go to dashboard</button>
        </div>
      </div>
    );
  }

  const { setupProject, tasks, buyerName } = data;
  const tenantName = myTenant?.tenantName ?? 'your organisation';
  const buyerLabel = buyerName ?? 'the Buyer';
  const phaseLabel = PHASE_LABELS[setupProject.currentPhase];

  // Order: ring ascending, then id (stable tiebreak — createdAt is not in the
  // payload; id ordering mirrors selectNextThree's existing tiebreak).
  const orderedTasks = [...tasks].sort((a, b) =>
    a.ring !== b.ring ? a.ring - b.ring : a.id.localeCompare(b.id),
  );

  // ── Row renderers (epic #1247) ─────────────────────────────────────────────
  // Accordion row for past + current rings (expandable, Start CTA). Extracted
  // verbatim from the old flat list so behaviour/testids/a11y are unchanged.
  const renderTaskRow = (task: WizardTask) => {
    const copy = TASK_COPY[task.taskType];
    const pill = STATUS_PILL[task.status];
    const isOpen = openTaskId === task.id;
    const panelId = `setup-task-panel-${task.id}`;
    const isDeferred = task.taskType === EXPORT_DEFERRED;
    const isTerminal = task.status === 'completed' || task.status === 'skipped';
    return (
      <li key={task.id} data-testid={`setup-task-${task.taskType}`} style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm, 8px)', background: 'var(--surface)',
      }}>
        <button
          id={`setup-task-toggle-${task.id}`}
          data-testid={`setup-task-toggle-${task.taskType}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenTaskId(isOpen ? null : task.id)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
            textAlign: 'left', fontFamily: 'var(--font-body)',
          }}
        >
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{copy.title}</span>
          <span
            data-testid={`setup-task-status-${task.taskType}`}
            style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: pill.bg, color: pill.fg }}
          >
            {pill.label}
          </span>
          <span aria-hidden style={{ color: 'var(--text-secondary)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>▾</span>
        </button>

        {isOpen && (
          <div id={panelId} role="region" aria-labelledby={`setup-task-toggle-${task.id}`} style={{ padding: '0 16px 16px' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 0 }}>
              {copy.description}
            </p>
            {copy.capabilitiesUnlocked.length > 0 && (
              <>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text)', margin: '8px 0 4px' }}>
                  Unlocks
                </p>
                <ul style={{ margin: '0 0 12px', paddingLeft: 18 }}>
                  {copy.capabilitiesUnlocked.map(cap => (
                    <li key={cap} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{cap}</li>
                  ))}
                </ul>
              </>
            )}
            {task.status === 'blocked' && (
              <p data-testid={`setup-task-blocked-${task.taskType}`} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error, #ef4444)', margin: '0 0 12px' }}>
                This task is blocked. Resolve the blocker, then try again.
              </p>
            )}
            {isDeferred ? (
              // #1274 — the task has no UX (withdrawn; no Start), but
              // when it's skip-allowed the user must be able to SKIP it so the
              // ring can reach all-terminal and the phase can advance. Without
              // this, a non-skippable+deferred task hard-walls the ring.
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span data-testid={`setup-task-deferred-${task.taskType}`} style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  Not available
                </span>
                {task.skipAllowed && !isTerminal && (
                  <button
                    data-testid={`setup-task-skip-${task.taskType}`}
                    onClick={() => void handleSkip(task.id)}
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                      padding: '6px 14px', background: 'transparent', color: 'var(--text-secondary)',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
                    }}
                  >
                    Skip for now
                  </button>
                )}
              </div>
            ) : (
              <button
                data-testid={`setup-task-start-${task.taskType}`}
                onClick={() => handleStart(task.taskType)}
                disabled={isTerminal}
                style={{ ...primaryBtnStyle, padding: '8px 18px', fontSize: 14, opacity: isTerminal ? 0.5 : 1, cursor: isTerminal ? 'not-allowed' : 'pointer' }}
              >
                {isTerminal ? 'Done' : copy.ctaLabel}
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  // Locked row for FUTURE rings (ring > currentPhase): static, greyed, no
  // toggle and no Start CTA. Visible (not hidden) per AC2, so the user sees
  // what's ahead without being able to act on it before the ring unlocks.
  const renderLockedRow = (task: WizardTask) => {
    const copy = TASK_COPY[task.taskType];
    return (
      <li key={task.id} data-testid={`setup-task-${task.taskType}`} style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm, 8px)', background: 'var(--surface)', opacity: 0.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
          <span aria-hidden style={{ color: 'var(--text-secondary)' }}>🔒</span>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)' }}>{copy.title}</span>
          <span
            data-testid={`locked-pill-${task.taskType}`}
            style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: 'rgba(107,114,128,0.15)', color: 'var(--text-secondary)' }}
          >
            Locked
          </span>
        </div>
      </li>
    );
  };

  // completionPct is the only reachable completion signal here: GET
  // /setup-project reuses getActiveForUser, which filters status to
  // in_progress|paused — a 'completed' project 204s and never loads. (R1 F5
  // non-issue: the status==='completed' branch is unreachable on this endpoint.)
  const allDone = setupProject.completionPct >= 100;

  // ── Completion view ──────────────────────────────────────────────────────────

  if (allDone) {
    return (
      <div data-testid="setup-wizard-complete" style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h1 style={titleStyle}>You&rsquo;re done.</h1>
          <p style={subtitleStyle}>{buyerLabel} has been notified.</p>
          <button style={primaryBtnStyle} onClick={() => navigate('/projects')}>Explore BluePrint</button>
        </div>
      </div>
    );
  }

  // ── Main wizard ──────────────────────────────────────────────────────────────

  return (
    <div data-testid="setup-wizard" style={pageStyle}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        {/* Header — focused mode: no global navigation. */}
        <div style={{ marginBottom: 8 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            Setting up BluePrint for
          </p>
          <h1 data-testid="setup-wizard-tenant" style={{ ...titleStyle, marginBottom: 4 }}>{tenantName}</h1>
        </div>

        {/* Progress strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)' }}>
            <div
              data-testid="setup-wizard-progress"
              style={{
                height: '100%', borderRadius: 4, background: 'var(--primary, #7c3aed)',
                width: `${Math.min(100, setupProject.completionPct)}%`, transition: 'width 300ms',
              }}
            />
          </div>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
            {setupProject.completionPct}% · {phaseLabel}
          </span>
        </div>

        {/* Ring-grouped sections (epic #1247, AC2/AC4) — the flat ungrouped
            list is gone. Past + current rings are interactive accordions;
            future rings render as a visibly-locked "Unlocks after Phase N"
            section (not hidden). Ring gating is client-side off the payload's
            currentPhase + per-task ring — no new backend query (recon R4c). */}
        {([0, 1, 2, 3] as const)
          .filter(ring => orderedTasks.some(t => t.ring === ring))
          .map(ring => {
            const ringTasks = orderedTasks.filter(t => t.ring === ring);
            const locked = ring > setupProject.currentPhase;
            const isCurrent = ring === setupProject.currentPhase;
            return (
              <section key={ring} data-testid={`ring-section-${ring}`} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: locked ? 'var(--text-secondary)' : 'var(--text)', margin: 0 }}>
                    Phase {ring + 1} · {PHASE_LABELS[ring]}
                  </h2>
                  {locked ? (
                    <span
                      data-testid={`ring-locked-${ring}`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}
                    >
                      🔒 Unlocks after Phase {ring}
                    </span>
                  ) : (
                    <span
                      data-testid={`ring-badge-${ring}`}
                      style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: isCurrent ? 'var(--primary)' : '#16a34a' }}
                    >
                      {isCurrent ? 'Current phase' : 'Done'}
                    </span>
                  )}
                </div>
                <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ringTasks.map(task => (locked ? renderLockedRow(task) : renderTaskRow(task)))}
                </ul>
              </section>
            );
          })}

        {/* Hand-back — always visible */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button
            data-testid="setup-hand-back"
            onClick={() => { setHandBackError(null); setHandBackMessage(''); setShowHandBack(true); }}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '9px 16px',
              background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
            }}
          >
            Hand back to {buyerLabel}
          </button>
        </div>
      </div>

      {/* Tech Lead invite modal — the SHARED component, identical to the
          dashboard OnboardingPanel's (#1247 follow-on). invite_technical_lead's
          CTA opens this instead of navigating to /settings/organisation. On
          success: close + refetch so the hub reflects the new task state. */}
      {showInviteModal && (
        <InviteTechnicalLeadModal
          setupProjectId={setupProject.id}
          onSuccess={() => { setShowInviteModal(false); void fetchWizard(); }}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {/* GitHub connect modal (#1274 F4 / #1277) — the shared setup-scoped
          connect, usable by the assigned TL (member) as well as the admin. On
          success: close + refetch so the hub reflects the cleared ring 1. */}
      {showGithubModal && (
        <SetupGithubConnectModal
          onSuccess={() => { setShowGithubModal(false); void fetchWizard(); }}
          onClose={() => setShowGithubModal(false)}
        />
      )}

      {/* Hand-back confirmation modal (mirrors OnboardingPanel dialog a11y) */}
      {showHandBack && (
        <div
          data-testid="hand-back-modal"
          style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => { if (!handingBack) setShowHandBack(false); }}
          onKeyDown={e => { if (e.key === 'Escape' && !handingBack) setShowHandBack(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="hand-back-title"
            style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg, 12px)', padding: 24, width: 440, maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 id="hand-back-title" style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
              Hand setup back to {buyerLabel}?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: '0 0 12px' }}>
              {buyerLabel} will be notified and will take over the remaining setup tasks. You can be re-invited later.
            </p>

            <label htmlFor="hand-back-message" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
              Add a note (optional)
            </label>
            <textarea
              id="hand-back-message"
              data-testid="hand-back-message"
              value={handBackMessage}
              onChange={e => setHandBackMessage(e.target.value)}
              maxLength={500}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', marginBottom: 12, border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
            />

            {handBackError && (
              <p data-testid="hand-back-error" style={{ fontSize: 13, color: 'var(--error, #ef4444)', margin: '0 0 12px' }}>{handBackError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                data-testid="hand-back-cancel"
                onClick={() => setShowHandBack(false)}
                disabled={handingBack}
                style={{ padding: '8px 14px', borderRadius: 6, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: handingBack ? 'not-allowed' : 'pointer', fontSize: 14, opacity: handingBack ? 0.6 : 1 }}
              >
                Keep going
              </button>
              <button
                data-testid="hand-back-confirm"
                onClick={() => void handleHandBack()}
                disabled={handingBack}
                style={{ padding: '8px 14px', borderRadius: 6, background: 'var(--primary, #7c3aed)', color: '#fff', border: 'none', fontWeight: 500, cursor: handingBack ? 'not-allowed' : 'pointer', fontSize: 14, opacity: handingBack ? 0.6 : 1 }}
              >
                {handingBack ? 'Handing back…' : 'Hand back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '48px 24px', background: 'var(--bg)',
};

const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 480, background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', padding: 40,
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0,
};

const subtitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '8px 0 24px',
};

const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, padding: '10px 24px',
  background: 'var(--primary, #7c3aed)', color: '#fff', border: 'none',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
